/**
 * Reversal-source panel for TransactionDetailModal.
 *
 * Shows a compact summary of the original entry, the recorded reversal
 * reason (parsed out of `REVERSAL: <reason>` description prefix when
 * present), and a brief description of the original's source kind.
 */
import type { EntryDetail, LedgerSource } from '../../api';

interface Props {
  detail: EntryDetail;
  source: Extract<LedgerSource, { kind: 'reversal' }>;
}

export function ReversalPanel({ detail, source }: Props) {
  const { original, originalSource } = source;
  const reversalReason = extractReversalReason(detail.entry.description);
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-small">
        <p className="font-semibold text-amber-700">This is a reversal entry.</p>
        {reversalReason && (
          <p className="mt-1 text-amber-700/80">
            <span className="font-medium">Reason:</span> {reversalReason}
          </p>
        )}
      </div>

      <div className="rounded-md border border-border bg-surface-raised p-3">
        <h3 className="mb-2 text-small font-semibold text-ink">Original entry</h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-small">
          <dt className="text-ink-muted">Date</dt>
          <dd className="text-right text-ink">{original.effectiveDate}</dd>
          <dt className="text-ink-muted">Description</dt>
          <dd className="text-right text-ink">{original.description}</dd>
          {original.dr !== '0' && (
            <>
              <dt className="text-ink-muted">Dr</dt>
              <dd className="text-right font-mono text-ink">{original.dr}</dd>
            </>
          )}
          {original.cr !== '0' && (
            <>
              <dt className="text-ink-muted">Cr</dt>
              <dd className="text-right font-mono text-warn">{original.cr}</dd>
            </>
          )}
          <dt className="text-ink-muted">Type</dt>
          <dd className="text-right text-ink">{original.type}</dd>
          {originalSource && originalSource.kind !== 'none' && (
            <>
              <dt className="text-ink-muted">Original source</dt>
              <dd className="text-right text-ink">{originalSource.kind}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

function extractReversalReason(description: string): string | null {
  const m = /^REVERSAL:\s*(.+)$/.exec(description);
  return m ? m[1]!.trim() : null;
}
